// PixelLab DSL: a compact, human-readable serialization for compositions.
//
// Grammar (informal):
//   directive  := '@' WIDTH 'x' HEIGHT  |  '#v' INTEGER  |  '#' COMMENT_REST
//   layer      := KIND '(' param? (',' param)* ')' bracket?
//   bracket    := '[' BLEND ',' OPACITY (',' ('on'|'off'))? ']'
//   param      := KEY '=' value
//   value      := number | hex | identifier | string | array | bool
//
// Reserved param key: 'seed' becomes the layer's seed property (not stored in
// params). Default seed of 0 is omitted on serialize.

import type {
  AppState,
  BlendMode,
  Layer,
  LayerKind,
  Resolution,
} from './types';
import { INITIAL_STATE, STATE_VERSION } from './types';
import { makeLayerId } from './state';

export type DslValue = number | string | boolean | DslValue[];

export type ParseOk<T> = { ok: true; value: T };
export type ParseErr = { ok: false; error: string; line?: number };
export type ParseResult<T> = ParseOk<T> | ParseErr;

export type ParsedComposition = {
  version: number;
  resolution: Resolution;
  layers: Layer[];
};

const VALID_BLENDS: readonly BlendMode[] = [
  'source-over',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'color-dodge',
  'color-burn',
  'hard-light',
  'soft-light',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity',
];

// ---- Parse -----------------------------------------------------------------

export function parseDsl(source: string): ParseResult<ParsedComposition> {
  const lines = source.split(/\r?\n/);
  let version: number = STATE_VERSION;
  let resolution: Resolution = { ...INITIAL_STATE.resolution };
  const layers: Layer[] = [];

  for (let li = 0; li < lines.length; li++) {
    const raw = lines[li] ?? '';
    const line = raw.replace(/^\s+|\s+$/g, '');
    if (line === '') continue;
    if (line.startsWith('@') || line.startsWith('#')) {
      const r = parseDirectiveLine(line);
      if (!r.ok) return errAt(r.error, li + 1);
      if (r.value.resolution) resolution = r.value.resolution;
      if (r.value.version !== undefined) version = r.value.version;
      continue;
    }
    const layer = parseLayerLine(line);
    if (!layer.ok) return errAt(layer.error, li + 1);
    layers.push(layer.value);
  }

  return { ok: true, value: { version, resolution, layers } };
}

function errAt(error: string, line: number): ParseErr {
  return { ok: false, error, line };
}

function parseDirectiveLine(
  line: string,
): ParseResult<{ resolution?: Resolution; version?: number }> {
  const tokens = line.split(/\s+/).filter(Boolean);
  let resolution: Resolution | undefined;
  let version: number | undefined;
  for (const t of tokens) {
    if (t.startsWith('@')) {
      const m = t.slice(1).match(/^(\d+)x(\d+)$/);
      if (!m) return { ok: false, error: `bad resolution: ${t}` };
      const w = parseInt(m[1] as string, 10);
      const h = parseInt(m[2] as string, 10);
      if (w <= 0 || h <= 0 || w > 4096 || h > 4096) {
        return { ok: false, error: `resolution out of range: ${t}` };
      }
      resolution = { w, h };
    } else if (t.startsWith('#v')) {
      const v = parseInt(t.slice(2), 10);
      if (Number.isNaN(v) || v <= 0) {
        return { ok: false, error: `bad version: ${t}` };
      }
      version = v;
    } else if (t.startsWith('#')) {
      // free-form comment; rest of line ignored
      break;
    } else {
      return { ok: false, error: `unexpected directive token: ${t}` };
    }
  }
  const out: { resolution?: Resolution; version?: number } = {};
  if (resolution) out.resolution = resolution;
  if (version !== undefined) out.version = version;
  return { ok: true, value: out };
}

function parseLayerLine(line: string): ParseResult<Layer> {
  const openParen = line.indexOf('(');
  if (openParen < 0) return { ok: false, error: `expected '(': ${line}` };
  const kind = line.slice(0, openParen).trim();
  if (!/^[a-z][a-z0-9_-]*$/i.test(kind)) {
    return { ok: false, error: `bad layer kind: ${kind}` };
  }
  const closeParen = findMatching(line, openParen, '(', ')');
  if (closeParen < 0) return { ok: false, error: `unterminated '(': ${line}` };
  const paramsStr = line.slice(openParen + 1, closeParen);
  const rest = line.slice(closeParen + 1).trim();

  let blend: BlendMode = 'source-over';
  let opacity = 1;
  let enabled = true;

  if (rest.length > 0) {
    if (!rest.startsWith('[')) {
      return { ok: false, error: `expected '[' or end of line, got: ${rest}` };
    }
    const closeBracket = findMatching(rest, 0, '[', ']');
    if (closeBracket < 0) return { ok: false, error: `unterminated '[': ${line}` };
    const trailing = rest.slice(closeBracket + 1).trim();
    if (trailing !== '') {
      return { ok: false, error: `unexpected trailing text: ${trailing}` };
    }
    const parts = splitTopLevel(rest.slice(1, closeBracket), ',').map((s) => s.trim());
    if (parts[0]) {
      const b = parts[0];
      if (!VALID_BLENDS.includes(b as BlendMode)) {
        return { ok: false, error: `unknown blend mode: ${b}` };
      }
      blend = b as BlendMode;
    }
    if (parts[1]) {
      const op = parseFloat(parts[1]);
      if (Number.isNaN(op)) return { ok: false, error: `bad opacity: ${parts[1]}` };
      opacity = clamp01(op);
    }
    if (parts[2]) {
      if (parts[2] === 'on') enabled = true;
      else if (parts[2] === 'off') enabled = false;
      else return { ok: false, error: `expected 'on' or 'off': ${parts[2]}` };
    }
  }

  const params = parseParamMap(paramsStr);
  if (!params.ok) return params;

  let seed = 0;
  if (typeof params.value['seed'] === 'number') {
    seed = params.value['seed'] as number;
    delete params.value['seed'];
  }

  return {
    ok: true,
    value: {
      id: makeLayerId(),
      kind: kind as LayerKind,
      params: params.value,
      blend,
      opacity,
      enabled,
      seed,
    },
  };
}

function parseParamMap(s: string): ParseResult<Record<string, DslValue>> {
  const out: Record<string, DslValue> = {};
  const parts = splitTopLevel(s, ',')
    .map((p) => p.trim())
    .filter(Boolean);
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq < 0) return { ok: false, error: `expected '=' in param: ${part}` };
    const key = part.slice(0, eq).trim();
    const valStr = part.slice(eq + 1).trim();
    if (!/^[a-z_][a-z0-9_]*$/i.test(key)) {
      return { ok: false, error: `bad param key: ${key}` };
    }
    const v = parseValue(valStr);
    if (!v.ok) return { ok: false, error: `param ${key}: ${v.error}` };
    out[key] = v.value;
  }
  return { ok: true, value: out };
}

function parseValue(s: string): ParseResult<DslValue> {
  if (s === 'true') return { ok: true, value: true };
  if (s === 'false') return { ok: true, value: false };

  // Hex color
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) {
    return { ok: true, value: s.toLowerCase() };
  }

  // Number
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) {
    return { ok: true, value: parseFloat(s) };
  }

  // Array
  if (s.startsWith('[') && s.endsWith(']')) {
    const inner = s.slice(1, -1);
    const items = splitTopLevel(inner, ',')
      .map((p) => p.trim())
      .filter(Boolean);
    const out: DslValue[] = [];
    for (const item of items) {
      const v = parseValue(item);
      if (!v.ok) return v;
      out.push(v.value);
    }
    return { ok: true, value: out };
  }

  // Quoted string
  if (
    (s.startsWith('"') && s.endsWith('"') && s.length >= 2) ||
    (s.startsWith("'") && s.endsWith("'") && s.length >= 2)
  ) {
    return { ok: true, value: s.slice(1, -1) };
  }

  // Bare identifier (allows pico-8, source-over, etc.)
  if (/^[a-z_][a-z0-9_-]*$/i.test(s)) return { ok: true, value: s };

  return { ok: false, error: `unparseable value: ${s}` };
}

function splitTopLevel(s: string, sep: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  let inStr = false;
  let strCh = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i] as string;
    if (inStr) {
      if (c === '\\' && i + 1 < s.length) {
        cur += c + (s[i + 1] as string);
        i++;
        continue;
      }
      if (c === strCh) inStr = false;
      cur += c;
      continue;
    }
    if (c === '"' || c === "'") {
      inStr = true;
      strCh = c;
      cur += c;
    } else if (c === '[' || c === '(') {
      depth++;
      cur += c;
    } else if (c === ']' || c === ')') {
      depth--;
      cur += c;
    } else if (c === sep && depth === 0) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

function findMatching(s: string, start: number, open: string, close: string): number {
  let depth = 0;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (c === open) depth++;
    else if (c === close) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

// ---- Serialize -------------------------------------------------------------

export function serializeDsl(state: AppState): string {
  const lines: string[] = [];
  lines.push(`@${state.resolution.w}x${state.resolution.h} #v${STATE_VERSION}`);
  for (const layer of state.layers) {
    lines.push(serializeLayer(layer));
  }
  return lines.join('\n');
}

export function serializeLayer(layer: Layer): string {
  const parts: string[] = [];
  for (const k of Object.keys(layer.params).sort()) {
    parts.push(`${k}=${formatValue(layer.params[k])}`);
  }
  if (layer.seed !== 0) parts.push(`seed=${layer.seed}`);
  let line = `${layer.kind}(${parts.join(', ')})`;
  const isDefault =
    layer.blend === 'source-over' && layer.opacity === 1 && layer.enabled === true;
  if (!isDefault) {
    const bp: string[] = [layer.blend, formatOpacity(layer.opacity)];
    if (!layer.enabled) bp.push('off');
    line += ` [${bp.join(', ')}]`;
  }
  return line;
}

function formatOpacity(o: number): string {
  if (o === 1) return '1';
  if (o === 0) return '0';
  // Trim trailing zeros: 0.500 → 0.5
  return parseFloat(o.toFixed(4)).toString();
}

function formatValue(v: unknown): string {
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '0';
    return String(v);
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') {
    if (/^#[0-9a-f]{3,6}$/i.test(v)) return v.toLowerCase();
    if (/^[a-z_][a-z0-9_-]*$/i.test(v)) return v;
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) return '[' + v.map(formatValue).join(', ') + ']';
  return JSON.stringify(v);
}
